import React, { useState, useEffect, useRef } from 'react';
import { FiUpload, FiCheck, FiFile, FiLoader } from 'react-icons/fi';
import api from '../../utils/api';
import '../../styles/mobileResponsive.css';

const font = "'Outfit', sans-serif";
const C = { text: '#1a0020', sub: '#64748b', border: '#e9d5ff', bg: '#fdf4ff', white: '#fff', accent: '#a855f7' };

const UploadAnswers = () => {
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [seats, setSeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingId, setUploadingId] = useState(null);
    const [msgs, setMsgs] = useState({});
    const fileInputs = useRef({});

    const load = async () => {
        try {
            const r = await api.get('/api/invigilator/my-assignments');
            setAssignments(r.data.assignments || []);
            
            // If an assignment is selected, refresh its data
            if (selectedAssignment) {
                const refreshed = r.data.assignments.find(a => a.id === selectedAssignment.id);
                if (refreshed) {
                    setSelectedAssignment(refreshed);
                    const sr = await api.get(`/api/invigilator/assignment/${refreshed.id}/seats`);
                    setSeats(sr.data.seats);
                }
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const selectHall = async (hall) => {
        setLoading(true);
        try {
            const sr = await api.get(`/api/invigilator/assignment/${hall.id}/seats`);
            setSeats(sr.data.seats);
            setSelectedAssignment(hall);
            setMsgs({});
        } catch (e) { 
            console.error(e); 
            alert('Failed to load hall data.');
        } finally { setLoading(false); }
    };

    const handleUpload = async (seatId) => {
        const file = fileInputs.current[seatId]?.files?.[0];
        if (!file) { setMsgs(p => ({ ...p, [seatId]: { type: 'error', text: 'Please select a file first' } })); return; }

        setUploadingId(seatId);
        setMsgs(p => ({ ...p, [seatId]: { type: 'info', text: 'Uploading...' } }));

        const formData = new FormData();
        formData.append('file', file);
        try {
            await api.post(`/api/invigilator/seats/${seatId}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMsgs(p => ({ ...p, [seatId]: { type: 'success', text: '✅ Uploaded successfully' } }));
            load();
        } catch (err) {
            setMsgs(p => ({ ...p, [seatId]: { type: 'error', text: err.response?.data?.message || 'Upload failed' } }));
        } finally { setUploadingId(null); }
    };

    if (loading && assignments.length === 0) return <div style={{ textAlign: 'center', padding: 60, color: C.sub, fontFamily: font }}>Loading assignments...</div>;

    // ─── LIST VIEW ───
    if (!selectedAssignment) return (
        <div style={{ fontFamily: font }}>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 6px' }}>Upload Answer Sheets</h1>
            <p style={{ color: C.sub, fontSize: 13, margin: '0 0 20px' }}>Select a hall to start uploading student answer sheets.</p>

            {assignments.length === 0 ? (
                <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: 50, textAlign: 'center' }}>
                    <p style={{ color: C.sub }}>No assignments found.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                    {assignments.map(a => (
                        <div key={a.id} onClick={() => selectHall(a)} className="card-hover" style={{
                            background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, 
                            padding: 20, cursor: 'pointer', boxShadow: '0 4px 12px rgba(168,85,247,0.05)'
                        }}>
                            <div style={{ fontSize: 20, fontWeight: 900, color: C.accent, marginBottom: 8 }}>Hall {a.hall_number}</div>
                            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 4 }}>{a.subject_name}</div>
                            <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>{a.subject_code} • {a.batch_name}</div>
                            <div style={{ 
                                padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', display: 'inline-block',
                                background: a.status === 'active' ? '#d1fae5' : a.status === 'ended' ? '#f1f5f9' : '#fef3c7',
                                color: a.status === 'active' ? '#059669' : a.status === 'ended' ? '#64748b' : '#d97706'
                            }}>
                                {a.status}
                            </div>
                            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}`, color: C.accent, fontSize: 12, fontWeight: 700 }}>
                                Upload Answers →
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── DETAIL VIEW (Upload) ───
    if (selectedAssignment.status !== 'active') return (
        <div style={{ fontFamily: font }}>
            <button onClick={() => setSelectedAssignment(null)} style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 800, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                ← Back to List
            </button>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 8px' }}>Hall {selectedAssignment.hall_number}</h1>
            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: '40px 20px', textAlign: 'center' }}>
                <FiUpload size={48} color={C.accent} style={{ opacity: 0.25, marginBottom: 16 }} />
                <h3 style={{ color: C.text, fontWeight: 800 }}>{selectedAssignment.status === 'ended' ? 'Exam Ended' : 'Exam Not Started'}</h3>
                <p style={{ color: C.sub, fontSize: 14 }}>You can only upload answer sheets for active exams. This hall is currently in <strong>{selectedAssignment.status}</strong> status.</p>
            </div>
        </div>
    );

    const uploaded = seats.filter(s => s.uploaded_file_path).length;

    return (
        <div style={{ fontFamily: font }}>
            <button onClick={() => setSelectedAssignment(null)} style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 800, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                ← Back to List
            </button>

            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 8px' }}>Hall {selectedAssignment.hall_number} — Upload</h1>
            <p style={{ color: C.sub, fontSize: 13, margin: '0 0 8px' }}>{selectedAssignment.subject_name} • {selectedAssignment.batch_name}</p>
            
            <div style={{ background: '#f3e8ff', border: `1px solid #d8b4fe`, borderRadius: 12, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#6b21a8', fontWeight: 600 }}>
                ℹ️ Student names are shown here so <em>you</em> can identify them on-site. Teachers will only see the anonymous ID.
            </div>

            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.sub, marginBottom: 8 }}>Upload Progress — {uploaded}/{seats.length} students</div>
                    <div style={{ background: '#ede9fe', borderRadius: 10, height: 10, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: 'linear-gradient(90deg,#c084fc,#a855f7)', borderRadius: 10, width: `${seats.length ? (uploaded / seats.length) * 100 : 0}%`, transition: 'width 0.4s' }} />
                    </div>
                </div>
                <div style={{ fontSize: 26, fontWeight: 900, color: C.accent, flexShrink: 0 }}>{uploaded}/{seats.length}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {seats.map(s => {
                    const m = msgs[s.id];
                    const done = !!s.uploaded_file_path;
                    const isUploading = uploadingId === s.id;
                    return (
                        <div key={s.id} style={{ background: C.white, borderRadius: 18, border: `2px solid ${done ? '#d8b4fe' : C.border}`, padding: 18, transition: 'border-color 0.2s' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <div>
                                    <div style={{ fontSize: 20, fontWeight: 900, color: C.accent }}>Seat {s.seat_number}</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{s.student_name}</div>
                                    <div style={{ fontSize: 11, color: C.sub }}>{s.roll_number}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '3px 10px', borderRadius: 20 }}>{s.anonymous_id}</div>
                                    {done && <div style={{ fontSize: 11, color: '#059669', fontWeight: 700, marginTop: 6 }}>✅ Uploaded</div>}
                                </div>
                            </div>

                            {m && (
                                <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 12, fontWeight: 700, background: m.type === 'success' ? '#d1fae5' : m.type === 'error' ? '#fee2e2' : '#e0f2fe', color: m.type === 'success' ? '#065f46' : m.type === 'error' ? '#991b1b' : '#0369a1' }}>{m.text}</div>
                            )}

                            {done && s.uploaded_file_path && (
                                <div style={{ padding: '8px 12px', borderRadius: 8, background: C.bg, fontSize: 11, color: C.sub, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiFile size={12} /> <span style={{ wordBreak: 'break-all' }}>{s.uploaded_file_path.split('/').pop()}</span>
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input type="file" ref={el => fileInputs.current[s.id] = el} accept="image/*,application/pdf" style={{ fontSize: 12, color: C.sub, width: '100%' }} />
                                <button onClick={() => handleUpload(s.id)} disabled={isUploading} style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#c084fc,#a855f7)', color: '#fff', fontWeight: 800, fontSize: 12, cursor: isUploading ? 'not-allowed' : 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: isUploading ? 0.7 : 1, width: '100%' }}>
                                    {isUploading ? <><FiLoader size={13} /> Uploading</> : <><FiUpload size={13} /> {done ? 'Re-Upload' : 'Upload'}</>}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            {seats.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: C.sub, fontSize: 13 }}>No seat allocations found.</div>
            )}
        </div>
    );
};

export default UploadAnswers;
